import {
  HeadlineBodyText,
  EscalatorText,
  BodyText,
  BodyTitleText,
} from "src/components/KinsaTypography";
import type { StripeThemeConfig } from "./stripe-checkout-content";

/**
 * Resolve a CSS custom property to its computed value.
 * Falls back to the provided default when running server-side (no DOM).
 */
function cssVar(name: string, fallback = ""): string {
  if (typeof document === "undefined") return fallback;
  return (
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim() || fallback
  );
}

/** Shared typography components — they use CSS vars internally so they adapt per brand. */
const clientTypography = {
  HeadlineBodyText,
  BodyText,
  EscalatorText,
  BodyTitleText,
};

/**
 * Build Stripe theme at runtime from the active brand's CSS variables.
 * Stripe Elements runs in an iframe and can't read the parent's CSS vars,
 * so we resolve them here and pass literal values.
 */
function buildTheme(): StripeThemeConfig {
  const bg = cssVar("--color-background");
  const surface = cssVar("--color-surface");
  const surfaceElevated = cssVar("--color-surface-elevated");
  const text = cssVar("--color-foreground");
  const textMuted = cssVar("--color-foreground-muted");
  const primary = cssVar("--color-primary");
  const accent = cssVar("--color-accent");
  const fontSans = cssVar("--font-sans");
  const danger = cssVar("--color-error", "#df1b41");

  // TODO: Split brand-specific overrides into separate files (e.g. packages/config-tailwind/brand/kinsa/stripe-theme.ts)
  // and layer them on top of buildTheme(). Hardcoded values below (colors, font sizes, shadows)
  // should come from tailwind config or CSS variables where possible.
  const isLight = process.env.NEXT_PUBLIC_BRAND === "kinsa";

  // Google Fonts URL — Next.js loads these via next/font, but Stripe's iframe
  // needs its own copy.
  const fontGoogleUrl = isLight
    ? "https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700&display=swap"
    : "https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@400;500;600;700&display=swap";

  return {
    appearance: {
      theme: isLight ? "stripe" : "flat",
      variables: {
        ...(isLight
          ? {
              tabIconColor: text,
              tabIconSelectedColor: text,
              tabLogoColor: text,
              tabLogoSelectedColor: text,
              spacingUnit: "0px",
              tabSpacing: "6px",
            }
          : {
              colorBackground: bg,
              // Owlet card icon renders pure white (#fff) so it matches the
              // Apple Pay badge next to it. Body text stays lavender.
              tabIconColor: "#ffffff",
              tabIconSelectedColor: "#ffffff",
              tabLogoColor: text,
              tabLogoSelectedColor: text,
              colorIcon: text,
            }),
        colorPrimary: primary,
        colorDanger: danger,
        colorText: text,
        fontFamily: fontSans,
        borderRadius: "8px",
      },
      rules: {
        ".Tab": {
          backgroundColor: isLight ? surfaceElevated : surface,
          marginBottom: "10px",
          border: isLight
            ? "1px solid #E5E7EB"
            : `1px solid ${textMuted}33`,
        },
        ".Tab--selected": {
          backgroundColor: isLight ? "#FEF9FF" : surface,
          // Owlet selected-tab border matches the OR divider; Kinsa keeps accent.
          border: `1px solid ${isLight ? accent : "#d4d4f966"}`,
          boxShadow: isLight
            ? `0 4px 8px rgba(134, 25, 143, 0.1)`
            : "none",
        },
        ".TabLabel": {
          color: text,
          fontFamily: fontSans,
          fontWeight: "400",
          fontSize: "15px",
          ...(isLight ? { letterSpacing: "0.2px", lineHeight: "16px" } : {}),
        },
        ".TabIcon": { color: isLight ? text : "#ffffff" },
        ".TabIcon--selected": { color: isLight ? text : "#ffffff" },
        ".Input": {
          ...(isLight ? {} : { backgroundColor: surface, color: text }),
          border: isLight ? "1px solid #E5E7EB" : "none",
          padding: isLight ? "10px" : "16px",
          fontFamily: fontSans,
          fontSize: isLight ? "13px" : "17px",
        },
        // Owlet placeholder color = Figma "Brand/Lapis/400" — not in code tokens yet
        ...(isLight ? {} : { ".Input::placeholder": { color: "#a8abeb" } }),
        ".Input:focus": {
          // Owlet: no focus border or ring — input stays seamless on the dark canvas.
          border: isLight ? `1px solid ${text}` : "none",
          boxShadow: isLight ? `0 0 0 2px rgba(29, 36, 37, 0.2)` : "none",
        },
        ".Label": {
          fontFamily: fontSans,
          fontWeight: isLight ? "500" : "400",
          fontSize: isLight ? "12px" : "14px",
          color: text,
          marginTop: "8px",
          ...(isLight ? { letterSpacing: "1.5px", lineHeight: "18px" } : {}),
        },
        ".Error": { color: danger },
      },
    },
    fonts: [{ cssSrc: fontGoogleUrl }],
    clientTypography,
    submitFooterClassName: isLight
      ? "fixed bottom-0 left-0 right-0 bg-background px-[24px] pt-[16px] pb-[var(--felt-safe-area-bottom,0px)] shadow-[0px_3px_15px_0px_rgba(0,0,0,0.09),0px_0px_1px_0px_rgba(189,189,189,0.5),0px_2px_4px_0px_rgba(195,194,194,0.5)]"
      : "mt-6 pb-[120px]",
    submitButtonClassName: isLight ? "rounded-full uppercase" : "rounded-lg",
    summaryCardTheme: isLight
      ? {
          backgroundColor: "bg-purple-100",
          activeButtonColor: "text-teal-700",
          dividerColor: "#F0DAF0",
        }
      : {
          backgroundColor: "bg-surface",
          activeButtonColor: "text-primary",
          dividerColor: "rgba(255,255,255,0.1)",
          // Owlet Figma: all summary text renders in on-surface (#D4D4F9).
          // Only the billing-cycle toggle uses primary (mint).
          labelColor: "text-foreground",
          trialColor: "text-foreground",
          padding: "24px",
          borderRadius: "16px",
          labelText: "Order Summary",
          labelClassName: "text-[14px] leading-[20px] tracking-normal normal-case",
          trialUseBodyStyle: true,
          trialCopyTemplate: (days) => `${days} day Free Trial`,
          priceBodyClassName: "text-on-surface",
          footerUseBodyStyle: true,
          footerClassName: "font-bold text-on-surface",
        },
  };
}

let cached: StripeThemeConfig | null = null;

export function getPaymentTheme(): StripeThemeConfig {
  if (!cached) cached = buildTheme();
  return cached;
}
